import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { FORGE_CHAIN_OPTIONS_ASDA_FIRST as CHAIN_OPTIONS } from '../../config/chains';
import { ForgeApiService } from '../../services/ForgeApiService';
import {
  DataCollectionDashboardResponse,
  DataCollectionPreviewImage,
  DataCollectionRequestedType,
  DataCollectionRequestRow,
  DataCollectionStatus,
  DataCollectionTargetScope,
} from '../../types';
import { normalizeMasterLabelUrl as normalizeMasterLabelLinkUrl } from '../../utils/masterLabelLinks';

const SCOPE_LABELS: Record<DataCollectionTargetScope, string> = {
  item_number: 'Item',
  normalized_barcode: 'Barcode',
  design: 'Design',
};

const TYPE_LABELS: Record<DataCollectionRequestedType, string> = {
  product_image: 'Product image',
  crops: 'Crops / scans',
};

type PhoneModePreference = 'auto' | 'desktop' | 'phone';
type DataCollectionChainOption = { id: number; label: string };
type DataCollectionRequesterOption = { email: string; label: string };
type CountedStatus = Exclude<DataCollectionStatus, never>;
const PHONE_MODE_STORAGE_KEY = 'forge-data-collection-phone-mode';
const DATA_COLLECTION_GROUP_BY = 'item_number';

function parseApiTimestampMs(value: string): number {
  const trimmed = value.trim();
  if (!trimmed) return NaN;
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed);
  return new Date(hasTimezone ? trimmed : `${trimmed}Z`).getTime();
}

function formatAge(value: string): string {
  const ageMs = Date.now() - parseApiTimestampMs(value);
  if (!Number.isFinite(ageMs) || ageMs < 0) return '-';
  const minutes = Math.floor(ageMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatTarget(row: DataCollectionRequestRow): string {
  if (row.itemNumber) return row.itemNumber;
  if (row.targetScope === 'normalized_barcode') return row.normalizedBarcode || row.targetKey;
  if (row.targetScope === 'design') return row.designId || row.targetKey;
  return row.targetKey;
}

const masterLabelTargetLinkStyle: React.CSSProperties = {
  color: '#3fb950',
  fontWeight: 800,
  textDecoration: 'none',
};

const masterLabelTargetFallbackStyle: React.CSSProperties = {
  color: 'var(--text-tertiary)',
  fontWeight: 800,
};

const phoneMasterLabelTargetStyle: React.CSSProperties = {
  display: 'inline-flex',
  color: '#3fb950',
  fontFamily: 'var(--font-mono)',
  fontSize: 'clamp(16px, 4.6vw, 19px)',
  fontWeight: 900,
  lineHeight: 1.1,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
};

const phoneMasterLabelTargetFallbackStyle: React.CSSProperties = {
  ...phoneMasterLabelTargetStyle,
  color: 'var(--text-tertiary)',
};

const MasterLabelTargetLink: React.FC<{
  row: DataCollectionRequestRow;
  linkStyle?: React.CSSProperties;
  fallbackStyle?: React.CSSProperties;
}> = ({
  row,
  linkStyle = masterLabelTargetLinkStyle,
  fallbackStyle = masterLabelTargetFallbackStyle,
}) => {
  const masterLabelUrl = normalizeMasterLabelLinkUrl(row.masterLabelUrl, {
    chainId: row.chainId,
    requireTarget: true,
  });
  if (!masterLabelUrl) {
    return <span style={fallbackStyle}>{formatTarget(row)}</span>;
  }
  return (
    <Link to={masterLabelUrl} style={linkStyle}>
      {formatTarget(row)}
    </Link>
  );
};

function getRowRequestIds(row: DataCollectionRequestRow): string[] {
  return row.requestIds?.length ? row.requestIds : [row.requestId];
}

function formatScopes(row: DataCollectionRequestRow): string {
  const scopes = row.targetScopes?.length ? row.targetScopes : [row.targetScope];
  return scopes.map((scope) => SCOPE_LABELS[scope] || scope).join(', ');
}

function getPreviewImages(row: DataCollectionRequestRow): DataCollectionPreviewImage[] {
  const images: DataCollectionPreviewImage[] = [];
  const seenUrls = new Set<string>();
  row.previewImages?.forEach((image) => {
    if (!image.url || seenUrls.has(image.url)) return;
    seenUrls.add(image.url);
    images.push(image);
  });
  if (row.previewVizUrl && !seenUrls.has(row.previewVizUrl)) {
    images.unshift({ url: row.previewVizUrl, label: 'Preview', kind: 'preview' });
  }
  return images;
}

function useIsPhoneView(): boolean {
  const getIsPhone = () => (typeof window === 'undefined' ? false : window.innerWidth <= 760);
  const [isPhone, setIsPhone] = useState(getIsPhone);
  useEffect(() => {
    const handleResize = () => setIsPhone(getIsPhone());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isPhone;
}

function getInitialPhoneModePreference(): PhoneModePreference {
  if (typeof window === 'undefined') return 'auto';
  const stored = window.localStorage.getItem(PHONE_MODE_STORAGE_KEY);
  return stored === 'desktop' || stored === 'phone' || stored === 'auto' ? stored : 'auto';
}

const headerCellStyle: React.CSSProperties = {
  padding: '8px 10px',
  textAlign: 'left',
  fontSize: 10,
  color: 'var(--text-tertiary)',
  fontWeight: 800,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  borderBottom: '1px solid var(--divider)',
};

const bodyCellStyle: React.CSSProperties = {
  padding: '9px 10px',
  borderBottom: '1px solid var(--divider)',
  color: 'var(--text-secondary)',
  fontSize: 12,
  verticalAlign: 'middle',
};

const previewCellStyle: React.CSSProperties = {
  ...bodyCellStyle,
  width: 118,
};

const selectStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  color: 'var(--text-secondary)',
  borderRadius: 6,
  height: 30,
  padding: '0 9px',
  fontSize: 12,
  fontWeight: 700,
};

const phoneSelectStyle: React.CSSProperties = {
  ...selectStyle,
  width: '100%',
  minWidth: 0,
  height: 34,
};

const actionButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  border: '1px solid #2ea043',
  background: 'rgba(46,160,67,0.12)',
  color: '#7ee787',
  borderRadius: 7,
  padding: '7px 10px',
  fontWeight: 800,
  fontSize: 11,
  textDecoration: 'none',
};

const COUNT_KEY_BY_STATUS = {
  open: 'openCount',
  completed: 'completedCount',
  reviewed: 'reviewedCount',
} as const;

const TOTAL_KEY_BY_STATUS = {
  open: 'totalOpen',
  completed: 'totalCompleted',
  reviewed: 'totalReviewed',
} as const;

function clampCount(value: number): number {
  return Math.max(0, value);
}

function transitionCount(
  value: number,
  status: CountedStatus,
  fromStatus: CountedStatus,
  toStatus: CountedStatus,
  amount: number
): number {
  if (status === fromStatus) return clampCount(value - amount);
  if (status === toStatus) return value + amount;
  return value;
}

function updateDashboardForTransition(
  dashboard: DataCollectionDashboardResponse | null,
  row: DataCollectionRequestRow,
  fromStatus: CountedStatus,
  toStatus: CountedStatus,
  visibleStatus: DataCollectionStatus | 'all'
): DataCollectionDashboardResponse | null {
  if (!dashboard) return dashboard;
  const requestIds = getRowRequestIds(row);
  const requestIdSet = new Set(requestIds);
  const amount = requestIds.length;
  const shouldKeepVisible = visibleStatus === 'all';
  const transitionStat = (
    stat: DataCollectionDashboardResponse['chainStats'][number],
    shouldUpdate: boolean
  ) =>
    shouldUpdate
      ? {
          ...stat,
          [COUNT_KEY_BY_STATUS[fromStatus]]: clampCount(
            stat[COUNT_KEY_BY_STATUS[fromStatus]] - amount
          ),
          [COUNT_KEY_BY_STATUS[toStatus]]: stat[COUNT_KEY_BY_STATUS[toStatus]] + amount,
        }
      : stat;
  const transitionedRows = shouldKeepVisible
    ? dashboard.rows.map((candidate) =>
        getRowRequestIds(candidate).some((requestId) => requestIdSet.has(requestId))
          ? { ...candidate, status: toStatus }
          : candidate
      )
    : dashboard.rows.filter(
        (candidate) => !getRowRequestIds(candidate).some((requestId) => requestIdSet.has(requestId))
      );

  return {
    ...dashboard,
    rows: transitionedRows,
    [TOTAL_KEY_BY_STATUS[fromStatus]]: transitionCount(
      dashboard[TOTAL_KEY_BY_STATUS[fromStatus]],
      fromStatus,
      fromStatus,
      toStatus,
      amount
    ),
    [TOTAL_KEY_BY_STATUS[toStatus]]: transitionCount(
      dashboard[TOTAL_KEY_BY_STATUS[toStatus]],
      toStatus,
      fromStatus,
      toStatus,
      amount
    ),
    chainStats: dashboard.chainStats.map((stat) =>
      transitionStat(stat, stat.key === String(row.chainId))
    ),
    requesterStats: dashboard.requesterStats.map((stat) =>
      transitionStat(stat, stat.key === (row.requestedBy || ''))
    ),
  };
}

function clearDashboardRows(
  dashboard: DataCollectionDashboardResponse | null
): DataCollectionDashboardResponse | null {
  return dashboard ? { ...dashboard, rows: [] } : dashboard;
}

function queueIndexAfterRemoving(
  rows: DataCollectionRequestRow[],
  row: DataCollectionRequestRow,
  currentIndex: number
): number {
  const requestIdSet = new Set(getRowRequestIds(row));
  const removedIndex = rows.findIndex((candidate) =>
    getRowRequestIds(candidate).some((requestId) => requestIdSet.has(requestId))
  );
  const nextLength = Math.max(rows.length - 1, 0);
  if (nextLength === 0) return 0;
  if (removedIndex === -1) return Math.min(currentIndex, nextLength - 1);
  return Math.min(removedIndex, nextLength - 1);
}

export const DataCollectionDashboard: React.FC = () => {
  const { token } = useAuthContext();
  const { showErrorToast } = useNotificationContext();
  const [dashboard, setDashboard] = useState<DataCollectionDashboardResponse | null>(null);
  const [status, setStatus] = useState<DataCollectionStatus | 'all'>('open');
  const [chainId, setChainId] = useState<'all' | number>('all');
  const [scope, setScope] = useState<DataCollectionTargetScope | 'all'>('all');
  const [requestedType, setRequestedType] = useState<DataCollectionRequestedType | 'all'>('all');
  const [requestedBy, setRequestedBy] = useState<string>('all');
  const [phoneMode, setPhoneMode] = useState<PhoneModePreference>(getInitialPhoneModePreference);
  const [arePhoneFiltersOpen, setArePhoneFiltersOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [queueIndex, setQueueIndex] = useState(0);
  const latestDashboardRequestId = useRef(0);
  const autoPhoneView = useIsPhoneView();
  const isPhoneView = phoneMode === 'phone' || (phoneMode === 'auto' && autoPhoneView);
  const filterKey = useMemo(
    () =>
      JSON.stringify({
        status,
        chainId,
        scope,
        requestedType,
        requestedBy,
        groupBy: DATA_COLLECTION_GROUP_BY,
      }),
    [chainId, requestedBy, requestedType, scope, status]
  );
  const currentFilterKey = useRef(filterKey);

  useEffect(() => {
    currentFilterKey.current = filterKey;
  }, [filterKey]);

  const loadDashboard = useCallback(
    async (options?: { background?: boolean; clearRows?: boolean; showErrors?: boolean }) => {
      const requestId = latestDashboardRequestId.current + 1;
      latestDashboardRequestId.current = requestId;
      if (options?.clearRows) {
        setDashboard(clearDashboardRows);
        setQueueIndex(0);
      }
      if (!options?.background) {
        setIsLoading(true);
      }
      try {
        const response = await ForgeApiService.fetchDataCollectionDashboard(token, {
          status,
          chainId: chainId === 'all' ? null : chainId,
          targetScope: scope,
          requestedType,
          requestedBy,
          groupBy: DATA_COLLECTION_GROUP_BY,
        });
        if (
          latestDashboardRequestId.current === requestId &&
          currentFilterKey.current === filterKey
        ) {
          setDashboard(response);
        }
      } catch (error) {
        if (latestDashboardRequestId.current === requestId && options?.showErrors !== false) {
          showErrorToast(error instanceof Error ? error.message : 'Failed to load data collection');
        }
      } finally {
        if (latestDashboardRequestId.current === requestId) {
          setIsLoading(false);
        }
      }
    },
    [chainId, filterKey, requestedBy, requestedType, scope, showErrorToast, status, token]
  );

  useEffect(() => {
    void loadDashboard({ clearRows: true });
  }, [loadDashboard]);

  const chainOptions = useMemo(() => {
    const fromStats =
      dashboard?.chainStats.map((stat) => Number(stat.key)).filter(Number.isFinite) ?? [];
    return [...new Set([...CHAIN_OPTIONS.map((chain) => chain.id), ...fromStats])]
      .sort((left, right) => left - right)
      .map((id) => ({
        id,
        label:
          dashboard?.chainStats.find((stat) => Number(stat.key) === id)?.label ||
          CHAIN_OPTIONS.find((chain) => chain.id === id)?.label ||
          `Chain ${id}`,
      }));
  }, [dashboard?.chainStats]);

  const rows = dashboard?.rows ?? [];
  const activeQueueRow = rows[Math.min(queueIndex, Math.max(rows.length - 1, 0))] ?? null;

  const requesterOptions = useMemo(
    () =>
      (dashboard?.requesterStats ?? [])
        .filter((stat) => stat.key)
        .map((stat) => ({
          email: stat.key,
          label: `${stat.label} (${stat.openCount}/${stat.completedCount}/${stat.reviewedCount})`,
        })),
    [dashboard?.requesterStats]
  );
  const hasSelectedRequesterOption =
    requestedBy === 'all' || requesterOptions.some((option) => option.email === requestedBy);
  const phoneFilterSummary = useMemo(() => {
    const chainLabel =
      chainId === 'all'
        ? 'All chains'
        : chainOptions.find((chain) => chain.id === chainId)?.label || `Chain ${chainId}`;
    const scopeLabel = scope === 'all' ? 'All targets' : SCOPE_LABELS[scope] || scope;
    const typeLabel =
      requestedType === 'all' ? 'All needs' : TYPE_LABELS[requestedType] || requestedType;
    const requesterLabel = requestedBy === 'all' ? 'All requesters' : requestedBy;
    return [
      status === 'all'
        ? 'All'
        : status === 'open'
          ? 'Open'
          : status === 'completed'
            ? 'Completed'
            : 'Reviewed',
      chainLabel,
      scopeLabel,
      typeLabel,
      requesterLabel,
    ].join(' · ');
  }, [chainId, chainOptions, requestedBy, requestedType, scope, status]);

  useEffect(() => {
    setQueueIndex((previous) => Math.min(previous, Math.max(rows.length - 1, 0)));
  }, [rows.length]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(PHONE_MODE_STORAGE_KEY, phoneMode);
  }, [phoneMode]);

  const handleComplete = async (row: DataCollectionRequestRow) => {
    const requestIds = getRowRequestIds(row);
    const previousDashboard = dashboard;
    const previousQueueIndex = queueIndex;
    const transitionFilterKey = filterKey;
    setCompletingId(row.requestId);
    setDashboard((currentDashboard) =>
      updateDashboardForTransition(currentDashboard, row, 'open', 'completed', status)
    );
    setQueueIndex((currentIndex) => queueIndexAfterRemoving(rows, row, currentIndex));
    try {
      await ForgeApiService.completeDataCollectionRequestsBatch(token, requestIds);
      if (currentFilterKey.current === transitionFilterKey) {
        void loadDashboard({ background: true, showErrors: false });
      }
    } catch (error) {
      if (currentFilterKey.current === transitionFilterKey) {
        setDashboard(previousDashboard);
        setQueueIndex(previousQueueIndex);
      }
      showErrorToast(error instanceof Error ? error.message : 'Failed to finish request');
    } finally {
      setCompletingId(null);
    }
  };

  const handleReview = async (row: DataCollectionRequestRow) => {
    const requestIds = getRowRequestIds(row);
    const previousDashboard = dashboard;
    const previousQueueIndex = queueIndex;
    const transitionFilterKey = filterKey;
    setReviewingId(row.requestId);
    setDashboard((currentDashboard) =>
      updateDashboardForTransition(currentDashboard, row, 'completed', 'reviewed', status)
    );
    setQueueIndex((currentIndex) => queueIndexAfterRemoving(rows, row, currentIndex));
    try {
      await ForgeApiService.reviewDataCollectionRequestsBatch(token, requestIds);
      if (currentFilterKey.current === transitionFilterKey) {
        void loadDashboard({ background: true, showErrors: false });
      }
    } catch (error) {
      if (currentFilterKey.current === transitionFilterKey) {
        setDashboard(previousDashboard);
        setQueueIndex(previousQueueIndex);
      }
      showErrorToast(error instanceof Error ? error.message : 'Failed to review request');
    } finally {
      setReviewingId(null);
    }
  };

  const goPrevious = () => setQueueIndex((previous) => Math.max(0, previous - 1));
  const goNext = () =>
    setQueueIndex((previous) => Math.min(Math.max(rows.length - 1, 0), previous + 1));

  return (
    <div
      className="page-content"
      style={{
        padding: isPhoneView ? 8 : 18,
        width: '100%',
        height: isPhoneView ? '100%' : undefined,
        minHeight: isPhoneView ? 0 : undefined,
        overflow: isPhoneView ? 'hidden' : undefined,
        display: isPhoneView ? 'flex' : undefined,
        flexDirection: isPhoneView ? 'column' : undefined,
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          marginBottom: isPhoneView ? 8 : 16,
          flexShrink: 0,
        }}
      >
        <div>
          <h1 style={{ margin: 0, color: 'var(--text-primary)', fontSize: isPhoneView ? 20 : 22 }}>
            Store Data Collection
          </h1>
          {!isPhoneView ? (
            <p style={{ margin: '6px 0 0', color: 'var(--text-tertiary)', fontSize: 12 }}>
              Open Forge requests for product images and crops / scans.
            </p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => void loadDashboard()}
          disabled={isLoading}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            height: 34,
            padding: '0 12px',
            border: '1px solid var(--border)',
            background: 'var(--surface)',
            color: 'var(--text-secondary)',
            borderRadius: 7,
            cursor: isLoading ? 'wait' : 'pointer',
            fontWeight: 800,
            flexShrink: 0,
          }}
        >
          <RefreshOutlinedIcon
            className={isLoading ? 'dc-refresh-icon-loading' : undefined}
            style={{ fontSize: 17 }}
          />
          Refresh
        </button>
      </div>

      {!isPhoneView ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, minmax(120px, 1fr))',
            gap: 10,
            marginBottom: 14,
          }}
        >
          <StatCard label="Open" value={dashboard?.totalOpen ?? 0} tone="#7ee787" />
          <StatCard label="Completed" value={dashboard?.totalCompleted ?? 0} tone="#58a6ff" />
          <StatCard label="Reviewed" value={dashboard?.totalReviewed ?? 0} tone="#d2a8ff" />
          <StatCard label="Chains" value={dashboard?.chainStats.length ?? 0} tone="#f2cc46" />
          <StatCard label="Visible rows" value={rows.length} tone="#ffab70" />
        </div>
      ) : null}

      {isPhoneView ? (
        <PhoneFilterPanel
          isOpen={arePhoneFiltersOpen}
          summary={phoneFilterSummary}
          status={status}
          chainId={chainId}
          scope={scope}
          requestedType={requestedType}
          requestedBy={requestedBy}
          phoneMode={phoneMode}
          chainOptions={chainOptions}
          requesterOptions={requesterOptions}
          hasSelectedRequesterOption={hasSelectedRequesterOption}
          onToggle={() => setArePhoneFiltersOpen((current) => !current)}
          onStatusChange={setStatus}
          onChainChange={setChainId}
          onScopeChange={setScope}
          onRequestedTypeChange={setRequestedType}
          onRequestedByChange={setRequestedBy}
          onPhoneModeChange={setPhoneMode}
        />
      ) : (
        <FilterControls
          status={status}
          chainId={chainId}
          scope={scope}
          requestedType={requestedType}
          requestedBy={requestedBy}
          phoneMode={phoneMode}
          chainOptions={chainOptions}
          requesterOptions={requesterOptions}
          hasSelectedRequesterOption={hasSelectedRequesterOption}
          onStatusChange={setStatus}
          onChainChange={setChainId}
          onScopeChange={setScope}
          onRequestedTypeChange={setRequestedType}
          onRequestedByChange={setRequestedBy}
          onPhoneModeChange={setPhoneMode}
        />
      )}

      {isPhoneView ? (
        <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
          <PhoneQueueView
            row={activeQueueRow}
            rowCount={rows.length}
            queueIndex={Math.min(queueIndex, Math.max(rows.length - 1, 0))}
            isLoading={isLoading}
            completingId={completingId}
            reviewingId={reviewingId}
            onPrevious={goPrevious}
            onNext={goNext}
            onComplete={handleComplete}
            onReview={handleReview}
          />
        </div>
      ) : (
        <DesktopTable
          rows={rows}
          isLoading={isLoading}
          completingId={completingId}
          reviewingId={reviewingId}
          onComplete={handleComplete}
          onReview={handleReview}
        />
      )}
    </div>
  );
};

interface FilterControlsProps {
  status: DataCollectionStatus | 'all';
  chainId: 'all' | number;
  scope: DataCollectionTargetScope | 'all';
  requestedType: DataCollectionRequestedType | 'all';
  requestedBy: string;
  phoneMode: PhoneModePreference;
  chainOptions: DataCollectionChainOption[];
  requesterOptions: DataCollectionRequesterOption[];
  hasSelectedRequesterOption: boolean;
  isPhone?: boolean;
  onStatusChange: (value: DataCollectionStatus | 'all') => void;
  onChainChange: (value: 'all' | number) => void;
  onScopeChange: (value: DataCollectionTargetScope | 'all') => void;
  onRequestedTypeChange: (value: DataCollectionRequestedType | 'all') => void;
  onRequestedByChange: (value: string) => void;
  onPhoneModeChange: (value: PhoneModePreference) => void;
}

const FilterControls: React.FC<FilterControlsProps> = ({
  status,
  chainId,
  scope,
  requestedType,
  requestedBy,
  phoneMode,
  chainOptions,
  requesterOptions,
  hasSelectedRequesterOption,
  isPhone = false,
  onStatusChange,
  onChainChange,
  onScopeChange,
  onRequestedTypeChange,
  onRequestedByChange,
  onPhoneModeChange,
}) => (
  <div
    style={{
      display: isPhone ? 'grid' : 'flex',
      gridTemplateColumns: isPhone ? 'repeat(2, minmax(0, 1fr))' : undefined,
      gap: isPhone ? 6 : 8,
      flexWrap: isPhone ? undefined : 'wrap',
      marginBottom: isPhone ? 0 : 14,
      flexShrink: 0,
      width: '100%',
    }}
  >
    <select
      aria-label="Status"
      value={status}
      onChange={(event) => onStatusChange(event.target.value as DataCollectionStatus | 'all')}
      style={isPhone ? phoneSelectStyle : selectStyle}
    >
      <option value="open">Open</option>
      <option value="completed">Completed</option>
      <option value="reviewed">Reviewed</option>
      <option value="all">All</option>
    </select>
    <select
      aria-label="Chain"
      value={chainId}
      onChange={(event) =>
        onChainChange(event.target.value === 'all' ? 'all' : Number(event.target.value))
      }
      style={isPhone ? phoneSelectStyle : selectStyle}
    >
      <option value="all">All chains</option>
      {chainOptions.map((chain) => (
        <option key={chain.id} value={chain.id}>
          {chain.label}
        </option>
      ))}
    </select>
    <select
      aria-label="Target"
      value={scope}
      onChange={(event) => onScopeChange(event.target.value as DataCollectionTargetScope | 'all')}
      style={isPhone ? phoneSelectStyle : selectStyle}
    >
      <option value="all">All targets</option>
      <option value="item_number">Item number</option>
      <option value="normalized_barcode">Normalized barcode</option>
      <option value="design">Design</option>
    </select>
    <select
      aria-label="Need"
      value={requestedType}
      onChange={(event) =>
        onRequestedTypeChange(event.target.value as DataCollectionRequestedType | 'all')
      }
      style={isPhone ? phoneSelectStyle : selectStyle}
    >
      <option value="all">All needs</option>
      <option value="product_image">Product image</option>
      <option value="crops">Crops / scans</option>
    </select>
    <select
      aria-label="Requester"
      value={requestedBy}
      onChange={(event) => onRequestedByChange(event.target.value)}
      style={isPhone ? phoneSelectStyle : selectStyle}
    >
      <option value="all">All requesters</option>
      {!hasSelectedRequesterOption ? <option value={requestedBy}>{requestedBy}</option> : null}
      {requesterOptions.map((requester) => (
        <option key={requester.email} value={requester.email}>
          {requester.label}
        </option>
      ))}
    </select>
    <select
      aria-label="Phone mode"
      value={phoneMode}
      onChange={(event) => onPhoneModeChange(event.target.value as PhoneModePreference)}
      style={isPhone ? phoneSelectStyle : selectStyle}
    >
      <option value="auto">Auto phone mode</option>
      <option value="desktop">Desktop mode</option>
      <option value="phone">Phone mode</option>
    </select>
  </div>
);

interface PhoneFilterPanelProps extends FilterControlsProps {
  isOpen: boolean;
  summary: string;
  onToggle: () => void;
}

const PhoneFilterPanel: React.FC<PhoneFilterPanelProps> = ({
  isOpen,
  summary,
  onToggle,
  ...filterProps
}) => (
  <div
    data-testid="data-collection-phone-filters"
    style={{
      border: '1px solid var(--border)',
      borderRadius: 8,
      background: 'var(--surface)',
      marginBottom: 8,
      overflow: 'hidden',
      flexShrink: 0,
    }}
  >
    <button
      type="button"
      aria-expanded={isOpen}
      onClick={onToggle}
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr)',
        gap: 8,
        alignItems: 'center',
        width: '100%',
        minHeight: 40,
        padding: '7px 10px',
        border: 0,
        background: 'transparent',
        color: 'var(--text-primary)',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <span style={{ fontSize: 12, fontWeight: 900 }}>Filters</span>
      <span
        style={{
          minWidth: 0,
          color: 'var(--text-tertiary)',
          fontSize: 11,
          fontWeight: 700,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {summary}
      </span>
    </button>
    {isOpen ? (
      <div style={{ padding: '0 10px 10px' }}>
        <FilterControls {...filterProps} isPhone />
      </div>
    ) : null}
  </div>
);

interface DesktopTableProps {
  rows: DataCollectionRequestRow[];
  isLoading: boolean;
  completingId: string | null;
  reviewingId: string | null;
  onComplete: (row: DataCollectionRequestRow) => void;
  onReview: (row: DataCollectionRequestRow) => void;
}

const DesktopTable: React.FC<DesktopTableProps> = ({
  rows,
  isLoading,
  completingId,
  reviewingId,
  onComplete,
  onReview,
}) => (
  <div
    style={{
      border: '1px solid var(--border)',
      borderRadius: 8,
      overflowX: 'auto',
      overflowY: 'hidden',
      background: 'var(--surface)',
    }}
  >
    <table style={{ width: '100%', minWidth: 860, borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={headerCellStyle}>Preview</th>
          <th style={headerCellStyle}>Chain</th>
          <th style={headerCellStyle}>Item</th>
          <th style={headerCellStyle}>Product</th>
          <th style={headerCellStyle}>Needs</th>
          <th style={headerCellStyle}>Requested</th>
          <th style={headerCellStyle}>Action</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.requestId}>
            <DesktopPreviewCell row={row} />
            <td style={bodyCellStyle}>{row.chainName || `Chain ${row.chainId}`}</td>
            <td style={{ ...bodyCellStyle, fontFamily: 'var(--font-mono)' }}>
              <MasterLabelTargetLink row={row} />
              <div
                style={{
                  color: 'var(--text-tertiary)',
                  fontFamily: 'var(--font-ui)',
                  fontSize: 10,
                  marginTop: 3,
                }}
              >
                {formatScopes(row)}
                {(row.requestCount ?? 1) > 1 ? ` · ${row.requestCount} requests` : ''}
              </div>
            </td>
            <td style={bodyCellStyle}>{row.productName || '-'}</td>
            <td style={bodyCellStyle}>
              <NeedChips row={row} />
            </td>
            <td style={bodyCellStyle}>
              <div>{formatAge(row.requestedAt)}</div>
              <div style={{ color: 'var(--text-tertiary)', fontSize: 10 }}>
                {row.requestedBy || '-'}
              </div>
            </td>
            <td style={bodyCellStyle}>
              <DataCollectionAction
                row={row}
                completingId={completingId}
                reviewingId={reviewingId}
                onComplete={onComplete}
                onReview={onReview}
              />
            </td>
          </tr>
        ))}
        {isLoading && rows.length === 0 ? (
          <DesktopSkeletonRows />
        ) : rows.length === 0 ? (
          <tr>
            <td
              colSpan={7}
              style={{ ...bodyCellStyle, textAlign: 'center', color: 'var(--text-tertiary)' }}
            >
              <EmptyState compact />
            </td>
          </tr>
        ) : null}
      </tbody>
    </table>
  </div>
);

const DesktopPreviewCell: React.FC<{ row: DataCollectionRequestRow }> = ({ row }) => {
  const previewImage = getPreviewImages(row)[0];
  return (
    <td style={previewCellStyle}>
      {previewImage?.url ? (
        <img
          src={previewImage.url}
          alt=""
          title={previewImage.label}
          style={{
            width: 88,
            height: 88,
            objectFit: 'contain',
            background: '#050505',
            borderRadius: 8,
          }}
        />
      ) : (
        <span style={{ color: 'var(--text-tertiary)' }}>-</span>
      )}
    </td>
  );
};

interface PhoneQueueViewProps {
  row: DataCollectionRequestRow | null;
  rowCount: number;
  queueIndex: number;
  isLoading: boolean;
  completingId: string | null;
  reviewingId: string | null;
  onPrevious: () => void;
  onNext: () => void;
  onComplete: (row: DataCollectionRequestRow) => void;
  onReview: (row: DataCollectionRequestRow) => void;
}

const PhoneQueueView: React.FC<PhoneQueueViewProps> = ({
  row,
  rowCount,
  queueIndex,
  isLoading,
  completingId,
  reviewingId,
  onPrevious,
  onNext,
  onComplete,
  onReview,
}) => {
  if (!row) {
    if (isLoading) {
      return <PhoneQueueSkeleton />;
    }
    return (
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 8,
          background: 'var(--surface)',
          color: 'var(--text-tertiary)',
          padding: 22,
          textAlign: 'center',
        }}
      >
        <EmptyState />
      </div>
    );
  }

  const canMoveBack = queueIndex > 0;
  const canMoveNext = queueIndex < rowCount - 1;
  const previewImages = getPreviewImages(row);
  const masterLabelUrl = normalizeMasterLabelLinkUrl(row.masterLabelUrl, {
    chainId: row.chainId,
    requireTarget: true,
  });

  return (
    <div
      data-testid="data-collection-phone-queue"
      style={{
        width: '100%',
        maxWidth: 'min(430px, 100%)',
        height: '100%',
        minHeight: 0,
        margin: '0 auto',
        display: 'grid',
        gridTemplateRows: 'minmax(0, 1fr) auto',
        gap: 8,
      }}
    >
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 8,
          background: 'var(--surface)',
          overflow: 'hidden',
          minHeight: 0,
          display: 'grid',
          gridTemplateRows: 'minmax(0, 1fr) auto',
        }}
      >
        <PhonePreviewCarousel images={previewImages} />
        <div style={{ padding: '10px 12px', minHeight: 0, overflow: 'auto' }}>
          <div
            style={{
              color: 'var(--text-tertiary)',
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              marginBottom: 4,
            }}
          >
            {queueIndex + 1} / {rowCount} · {row.chainName || `Chain ${row.chainId}`}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              flexWrap: 'wrap',
              columnGap: 10,
              rowGap: 4,
            }}
          >
            <div
              style={{
                color: 'var(--text-primary)',
                fontSize: 'clamp(17px, 5.3vw, 21px)',
                fontWeight: 900,
                lineHeight: 1.12,
                wordBreak: 'break-word',
              }}
            >
              {row.productName || 'Unknown product'}
            </div>
            <MasterLabelTargetLink
              row={row}
              linkStyle={phoneMasterLabelTargetStyle}
              fallbackStyle={phoneMasterLabelTargetFallbackStyle}
            />
          </div>
          <div style={{ marginTop: 8 }}>
            <NeedChips row={row} />
          </div>
          <DetailList row={row} compact />
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 8,
          padding: 0,
          paddingBottom: 'env(safe-area-inset-bottom)',
          background: 'rgba(15,15,15,0.96)',
        }}
      >
        <button
          type="button"
          onClick={onPrevious}
          disabled={!canMoveBack}
          style={{
            ...queueButtonStyle,
            opacity: canMoveBack ? 1 : 0.45,
            cursor: canMoveBack ? 'pointer' : 'not-allowed',
          }}
        >
          <ArrowBackOutlinedIcon style={{ fontSize: 17 }} />
          Previous
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!canMoveNext}
          style={{
            ...queueButtonStyle,
            opacity: canMoveNext ? 1 : 0.45,
            cursor: canMoveNext ? 'pointer' : 'not-allowed',
          }}
        >
          Skip
          <ArrowForwardOutlinedIcon style={{ fontSize: 17 }} />
        </button>
        {masterLabelUrl ? (
          <Link to={masterLabelUrl} style={{ ...queueButtonStyle, textDecoration: 'none' }}>
            <OpenInNewOutlinedIcon style={{ fontSize: 17 }} />
            Open
          </Link>
        ) : (
          <button
            type="button"
            disabled
            style={{
              ...queueButtonStyle,
              cursor: 'not-allowed',
              opacity: 0.45,
            }}
          >
            <OpenInNewOutlinedIcon style={{ fontSize: 17 }} />
            Open
          </button>
        )}
        <DataCollectionAction
          row={row}
          completingId={completingId}
          reviewingId={reviewingId}
          onComplete={onComplete}
          onReview={onReview}
          style={{ width: '100%', height: 40 }}
        />
      </div>
    </div>
  );
};

const SkeletonBlock: React.FC<{
  width?: number | string;
  height: number | string;
  radius?: number;
  style?: React.CSSProperties;
}> = ({ width = '100%', height, radius = 6, style }) => (
  <div
    className="dc-skeleton"
    style={{
      width,
      height,
      borderRadius: radius,
      ...style,
    }}
  />
);

const PhoneQueueSkeleton: React.FC = () => (
  <div
    data-testid="data-collection-phone-skeleton"
    style={{
      width: '100%',
      maxWidth: 'min(430px, 100%)',
      height: '100%',
      minHeight: 0,
      margin: '0 auto',
      display: 'grid',
      gridTemplateRows: 'minmax(0, 1fr) auto',
      gap: 8,
    }}
  >
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 8,
        background: 'var(--surface)',
        overflow: 'hidden',
        minHeight: 0,
        display: 'grid',
        gridTemplateRows: 'minmax(0, 1fr) auto',
      }}
    >
      <div style={{ background: '#050505', padding: 12, minHeight: 0 }}>
        <SkeletonBlock height="100%" radius={8} />
      </div>
      <div style={{ padding: '10px 12px 12px', display: 'grid', gap: 9 }}>
        <SkeletonBlock width="42%" height={12} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <SkeletonBlock width="56%" height={23} />
          <SkeletonBlock width="28%" height={20} />
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <SkeletonBlock width={86} height={22} radius={999} />
          <SkeletonBlock width={72} height={22} radius={999} />
        </div>
        <SkeletonBlock width="88%" height={14} />
        <SkeletonBlock width="72%" height={14} />
        <SkeletonBlock width="78%" height={14} />
      </div>
    </div>
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 8,
        paddingBottom: 'env(safe-area-inset-bottom)',
        background: 'rgba(15,15,15,0.96)',
      }}
    >
      {Array.from({ length: 4 }).map((_, index) => (
        <SkeletonBlock key={index} height={42} radius={7} />
      ))}
    </div>
  </div>
);

const DesktopSkeletonRows: React.FC = () => (
  <>
    {Array.from({ length: 5 }).map((_, rowIndex) => (
      <tr key={rowIndex} data-testid="data-collection-desktop-skeleton-row">
        <td style={previewCellStyle}>
          <SkeletonBlock width={88} height={88} radius={8} />
        </td>
        <td style={bodyCellStyle}>
          <SkeletonBlock width="72%" height={14} />
        </td>
        <td style={bodyCellStyle}>
          <SkeletonBlock width={96} height={16} />
          <SkeletonBlock width={128} height={10} style={{ marginTop: 6 }} />
        </td>
        <td style={bodyCellStyle}>
          <SkeletonBlock width="74%" height={16} />
        </td>
        <td style={bodyCellStyle}>
          <div style={{ display: 'flex', gap: 5 }}>
            <SkeletonBlock width={74} height={22} radius={999} />
            <SkeletonBlock width={56} height={22} radius={999} />
          </div>
        </td>
        <td style={bodyCellStyle}>
          <SkeletonBlock width={62} height={14} />
          <SkeletonBlock width={136} height={10} style={{ marginTop: 6 }} />
        </td>
        <td style={bodyCellStyle}>
          <SkeletonBlock width={92} height={32} radius={7} />
        </td>
      </tr>
    ))}
  </>
);

const EmptyState: React.FC<{ compact?: boolean }> = ({ compact = false }) => (
  <div
    style={{
      display: 'grid',
      placeItems: 'center',
      gap: 6,
      minHeight: compact ? 72 : 160,
      color: 'var(--text-tertiary)',
      textAlign: 'center',
    }}
  >
    <div style={{ fontSize: compact ? 12 : 13, fontWeight: 900 }}>No data collection requests</div>
    {!compact ? (
      <div style={{ fontSize: 11, maxWidth: 260 }}>
        Change filters or refresh when more work is expected.
      </div>
    ) : null}
  </div>
);

const PhonePreviewCarousel: React.FC<{ images: DataCollectionPreviewImage[] }> = ({ images }) => {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const activeIndex = Math.min(index, Math.max(images.length - 1, 0));
  const activeImage = images[activeIndex] ?? null;
  const hasMultipleImages = images.length > 1;
  const imageUrlsKey = useMemo(() => images.map((image) => image.url).join('|'), [images]);

  useEffect(() => {
    setIndex(0);
  }, [imageUrlsKey]);

  const goPrevious = () =>
    setIndex((current) => (images.length ? (current + images.length - 1) % images.length : 0));
  const goNext = () => setIndex((current) => (images.length ? (current + 1) % images.length : 0));

  return (
    <div
      style={{
        position: 'relative',
        background: '#050505',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 0,
        overflow: 'hidden',
        touchAction: 'pan-y',
      }}
      onTouchStart={(event) => {
        touchStartX.current = event.touches[0]?.clientX ?? null;
      }}
      onTouchEnd={(event) => {
        if (touchStartX.current === null || !hasMultipleImages) return;
        const deltaX =
          (event.changedTouches[0]?.clientX ?? touchStartX.current) - touchStartX.current;
        touchStartX.current = null;
        if (Math.abs(deltaX) < 36) return;
        if (deltaX < 0) {
          goNext();
        } else {
          goPrevious();
        }
      }}
    >
      {activeImage ? (
        <>
          <img
            src={activeImage.url}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
          <div
            style={{
              position: 'absolute',
              left: 8,
              right: 8,
              bottom: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                minWidth: 0,
                border: '1px solid rgba(255,255,255,0.22)',
                borderRadius: 999,
                background: 'rgba(0,0,0,0.72)',
                color: '#fff',
                fontSize: 11,
                fontWeight: 900,
                maxWidth: '70%',
                overflow: 'hidden',
                padding: '4px 8px',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {activeImage.label}
            </div>
            {hasMultipleImages ? (
              <div
                style={{
                  border: '1px solid rgba(255,255,255,0.18)',
                  borderRadius: 999,
                  background: 'rgba(0,0,0,0.72)',
                  color: 'rgba(255,255,255,0.86)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  fontWeight: 900,
                  padding: '4px 7px',
                }}
              >
                {activeIndex + 1}/{images.length}
              </div>
            ) : null}
          </div>
          {hasMultipleImages ? (
            <>
              <button
                type="button"
                aria-label="Previous preview image"
                onClick={goPrevious}
                style={{
                  ...previewNavButtonStyle,
                  left: 8,
                }}
              >
                ‹
              </button>
              <button
                type="button"
                aria-label="Next preview image"
                onClick={goNext}
                style={{
                  ...previewNavButtonStyle,
                  right: 8,
                }}
              >
                ›
              </button>
            </>
          ) : null}
        </>
      ) : (
        <span style={{ color: 'var(--text-tertiary)', fontWeight: 800 }}>No preview</span>
      )}
    </div>
  );
};

const NeedChips: React.FC<{ row: DataCollectionRequestRow }> = ({ row }) => (
  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
    {row.requestedTypes.map((type) => (
      <span
        key={type}
        style={{
          border: '1px solid var(--border)',
          borderRadius: 999,
          padding: '3px 7px',
          color: 'var(--text-primary)',
          fontSize: 10,
          fontWeight: 800,
        }}
      >
        {TYPE_LABELS[type] || type}
      </span>
    ))}
  </div>
);

const DetailList: React.FC<{ row: DataCollectionRequestRow; compact?: boolean }> = ({
  row,
  compact = false,
}) => {
  const normalizedBarcodes = row.normalizedBarcodes?.length
    ? row.normalizedBarcodes
    : row.normalizedBarcode
      ? [row.normalizedBarcode]
      : [];
  const designIds = row.designIds?.length ? row.designIds : row.designId ? [row.designId] : [];
  return (
    <div
      style={{
        display: 'grid',
        gap: compact ? 7 : 8,
        marginTop: compact ? 11 : 16,
        color: 'var(--text-secondary)',
        fontSize: compact ? 13 : 13,
        lineHeight: compact ? 1.25 : undefined,
      }}
    >
      <DetailRow
        label="Requested"
        value={`${formatAge(row.requestedAt)} · ${row.requestedBy || '-'}`}
      />
      <DetailRow label="Target" value={formatScopes(row)} />
      {normalizedBarcodes.length ? (
        <DetailRow label="Barcodes" value={normalizedBarcodes.join(', ')} mono />
      ) : null}
      {designIds.length ? <DetailRow label="Designs" value={designIds.join(', ')} mono /> : null}
    </div>
  );
};

const DetailRow: React.FC<{ label: string; value: string; mono?: boolean }> = ({
  label,
  value,
  mono,
}) => (
  <div style={{ display: 'grid', gridTemplateColumns: '96px 1fr', gap: 8 }}>
    <div style={{ color: 'var(--text-tertiary)', fontWeight: 800 }}>{label}</div>
    <div style={{ fontFamily: mono ? 'var(--font-mono)' : undefined, wordBreak: 'break-word' }}>
      {value}
    </div>
  </div>
);

const DataCollectionAction: React.FC<{
  row: DataCollectionRequestRow;
  completingId: string | null;
  reviewingId: string | null;
  onComplete: (row: DataCollectionRequestRow) => void;
  onReview: (row: DataCollectionRequestRow) => void;
  style?: React.CSSProperties;
}> = ({ row, completingId, reviewingId, onComplete, onReview, style }) => {
  if (row.status === 'reviewed') {
    return (
      <span style={{ color: 'var(--text-tertiary)' }}>
        {row.reviewedBy ? `Reviewed by ${row.reviewedBy}` : 'Reviewed'}
      </span>
    );
  }

  if (row.status === 'completed') {
    return (
      <button
        type="button"
        onClick={() => onReview(row)}
        disabled={reviewingId === row.requestId}
        style={{
          ...actionButtonStyle,
          ...style,
          cursor: reviewingId === row.requestId ? 'wait' : 'pointer',
        }}
      >
        <CheckCircleOutlineOutlinedIcon style={{ fontSize: 16 }} />
        Reviewed
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onComplete(row)}
      disabled={completingId === row.requestId}
      style={{
        ...actionButtonStyle,
        ...style,
        cursor: completingId === row.requestId ? 'wait' : 'pointer',
      }}
    >
      <CheckCircleOutlineOutlinedIcon style={{ fontSize: 16 }} />
      Finished
    </button>
  );
};

const previewNavButtonStyle: React.CSSProperties = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  width: 34,
  height: 44,
  border: '1px solid rgba(255,255,255,0.18)',
  borderRadius: 999,
  background: 'rgba(0,0,0,0.55)',
  color: '#fff',
  cursor: 'pointer',
  fontSize: 28,
  fontWeight: 700,
  lineHeight: 1,
};

const queueButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  minHeight: 42,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-secondary)',
  borderRadius: 7,
  fontWeight: 900,
  fontSize: 12,
};

const StatCard: React.FC<{ label: string; value: number; tone: string }> = ({
  label,
  value,
  tone,
}) => (
  <div
    style={{
      border: '1px solid var(--border)',
      borderRadius: 8,
      background: 'var(--surface)',
      padding: 12,
    }}
  >
    <div
      style={{
        color: 'var(--text-tertiary)',
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </div>
    <div
      style={{
        color: tone,
        fontFamily: 'var(--font-mono)',
        fontSize: 22,
        fontWeight: 900,
        marginTop: 5,
      }}
    >
      {value.toLocaleString()}
    </div>
  </div>
);
